import { UserSearch, CalendarCheck, Clock, TrendingUp } from 'lucide-react';
import { useDashboard } from '../../../hooks/queries/useDashboard';

interface ManagerOverviewProps {
  mode: 'functional' | 'preview';
  onStartReview?: () => void;
  onNavigateToInterviews?: () => void;
  onSetCalendar?: () => void;
}

export default function ManagerOverview({
  mode,
  onStartReview,
  onNavigateToInterviews,
  onSetCalendar,
}: ManagerOverviewProps) {
  const isPreview = mode === 'preview';
  const { data: dashboard, isLoading } = useDashboard();

  const reviewCount = dashboard?.pipeline?.new?.length ?? 0;
  const interviewCount = dashboard?.pipeline?.interview?.length ?? 0;
  const avgSpeed = dashboard?.kpis?.avgReviewTime?.label ?? '—';
  const retention = dashboard?.kpis?.retention90Day?.label ?? '—';

  return (
    <div className="bg-gray-50 p-4">
      {/* Section A: Stats Strip (Compact) */}
      <section className="grid grid-cols-2 gap-3 mb-6">
        {/* Card 1: Avg Speed */}
        <div className="bg-white rounded-xl shadow-sm p-3 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-gray-400 uppercase mb-1">Avg Speed</div>
            <div className="text-2xl font-extrabold text-gray-900">
              {isLoading ? '…' : avgSpeed}
            </div>
          </div>
          <TrendingUp className="w-6 h-6 text-emerald-500" />
        </div>

        {/* Card 2: 90-Day Retention */}
        <div className="bg-white rounded-xl shadow-sm p-3 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-gray-400 uppercase mb-1">90-Day Retention</div>
            <div className="text-2xl font-extrabold text-[#0156FF]">
              {isLoading ? '…' : retention}
            </div>
          </div>
        </div>
      </section>

      {/* Section B: Action Buttons (Main Focus) */}
      <section className="flex flex-col gap-3 mt-6">
        {/* Button 1: Review Applicants */}
        <button
          type="button"
          onClick={() => !isPreview && onStartReview?.()}
          disabled={isPreview}
          className={`relative bg-white rounded-2xl shadow-sm flex items-center gap-4 px-4 py-4 transition-shadow ${
            isPreview ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'
          }`}
        >
          <UserSearch className="w-10 h-10 text-[#0156FF] flex-shrink-0" strokeWidth={2} />
          <span className="text-base font-semibold text-gray-900 flex-1 text-left">Review Applicants</span>
          {reviewCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
              {reviewCount}
            </span>
          )}
        </button>

        {/* Button 2: Manage Interviews */}
        <button
          type="button"
          onClick={() => !isPreview && onNavigateToInterviews?.()}
          disabled={isPreview}
          className={`relative bg-white rounded-2xl shadow-sm flex items-center gap-4 px-4 py-4 transition-shadow ${
            isPreview ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'
          }`}
        >
          <CalendarCheck className="w-10 h-10 text-[#0156FF] flex-shrink-0" strokeWidth={2} />
          <span className="text-base font-semibold text-gray-900 flex-1 text-left">Manage Interviews</span>
          {interviewCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
              {interviewCount}
            </span>
          )}
        </button>

        {/* Button 3: Set Schedule */}
        <button
          type="button"
          onClick={() => !isPreview && onSetCalendar?.()}
          disabled={isPreview}
          className={`relative bg-white rounded-2xl shadow-sm flex items-center gap-4 px-4 py-4 transition-shadow ${
            isPreview ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'
          }`}
        >
          <Clock className="w-10 h-10 text-[#0156FF] flex-shrink-0" strokeWidth={2} />
          <span className="text-base font-semibold text-gray-900 flex-1 text-left">Set Schedule</span>
        </button>
      </section>

    </div>
  );
}
